/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { useEffect } from "react";

import { reportClientErrorAsync } from "../../utils/error-reporting.utils";

interface GlobalErrorHandlerProps {
	serverUrl?: string;
	basePath?: string;
}

export function GlobalErrorHandler({ serverUrl, basePath }: GlobalErrorHandlerProps = {}): null {
	useEffect(() => {
		// Handle unhandled promise rejections
		const handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
			console.error("Unhandled promise rejection:", event.reason);

			const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));

			void reportClientErrorAsync(error, { serverUrl, basePath });
		};

		// Handle uncaught JavaScript errors
		const handleUncaughtError = (event: ErrorEvent): void => {
			console.error("Uncaught error:", event.error);

			const error = event.error instanceof Error ? event.error : new Error(event.message || "Unknown error");

			void reportClientErrorAsync(error, { serverUrl, basePath });
		};

		// Add event listeners
		window.addEventListener("unhandledrejection", handleUnhandledRejection);
		window.addEventListener("error", handleUncaughtError);

		// Cleanup function
		return () => {
			window.removeEventListener("unhandledrejection", handleUnhandledRejection);
			window.removeEventListener("error", handleUncaughtError);
		};
	}, [basePath, serverUrl]);

	return null; // This component doesn't render anything
}
